/**
 * The top of a Word document as a document of its own: the body cut to its
 * first blocks, the images and headers those blocks and their section name,
 * and the styles, numbering and theme that lay them out. The rest of the body,
 * and every image past the cut, are never fetched.
 */

import { getOpcRelationshipPartName } from '@/lib/ooxml/package'
import { type OpenZip, readEntryHead } from '@/lib/ooxml/rangeArchive'
import {
  collectDependencies,
  decodeParts,
  officeDocumentPath,
  packStore,
  PREFETCH_MAX_BYTES,
  prefetchParts,
  parseRels,
  readParts,
  referencedRelIds,
  resolveTarget,
  rewriteContentTypes,
  rootRelsFor,
  writeRels,
} from '@/lib/ooxml/slim/package'

/**
 * A few more top-level blocks than the viewer's thumbnail mounts (twelve);
 * the viewer never mounts past that in a card, so the rest would only be parsed.
 */
export const DOCX_PREVIEW_BLOCKS = 16
/** Past this much body text the first page is long since full. */
const DOCX_PREVIEW_MAX_CHARS = 2 * 1024 * 1024
/** Notes and styles past this are someone's embedded payload, not formatting. */
const SUPPORT_PART_MAX_BYTES = 1024 * 1024

const DOCX_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/** Parts the layout reads whole; their own relationships (fonts, templates) are dropped. */
const SUPPORT_TYPES = new Set([
  'styles',
  'numbering',
  'settings',
  'theme',
  'fontTable',
  'footnotes',
  'endnotes',
])

const TAG = /<(\/?)([A-Za-z][\w:.-]*)\b[^>]*?(\/?)>/g

interface BodyScan {
  /** Offset just past `<w:body>`, or -1 before it has streamed in. */
  bodyStart: number
  /** Offsets just past each complete top-level block. */
  blockEnds: number[]
  /** The body's closing tag was reached. */
  closed: boolean
  /** The open top-level block, for cutting a table at its last whole row. */
  openBlock: { name: string; start: number; lastRowEnd: number } | null
}

/**
 * Walks the body's tags once as text streams in, counting top-level blocks.
 * Resumes where the last call left off, so streaming stays linear.
 */
const bodyScanner = () => {
  const scan: BodyScan = {
    bodyStart: -1,
    blockEnds: [],
    closed: false,
    openBlock: null,
  }
  let cursor = 0
  let depth = 0
  const advance = (text: string) => {
    if (scan.bodyStart < 0) {
      const open = /<w:body\b[^>]*>/.exec(text)
      if (!open) return scan
      scan.bodyStart = open.index + open[0].length
      cursor = scan.bodyStart
    }
    TAG.lastIndex = cursor
    for (let match = TAG.exec(text); match; match = TAG.exec(text)) {
      const [whole, closing, name, selfClosing] = match
      const end = match.index + whole.length
      cursor = end
      if (closing) {
        depth -= 1
        if (depth < 0) {
          scan.closed = true
          break
        }
        if (depth === 1 && name === 'w:tr' && scan.openBlock)
          scan.openBlock.lastRowEnd = end
        if (depth === 0) {
          scan.openBlock = null
          if (name !== 'w:sectPr') scan.blockEnds.push(end)
        }
      } else if (selfClosing) {
        if (depth === 0 && name !== 'w:sectPr') scan.blockEnds.push(end)
      } else {
        if (depth === 0)
          scan.openBlock = { name, start: match.index, lastRowEnd: -1 }
        depth += 1
      }
    }
    return scan
  }
  return advance
}

/** The first section's properties: a section break in the head, else the body's own. */
const firstSectPr = (body: string) =>
  /<w:sectPr\b[\s\S]*?<\/w:sectPr>/.exec(body)?.[0] ?? ''

interface CutBody {
  xml: string
  truncated: boolean
}

const cutDocument = (
  head: string,
  complete: boolean,
  scan: BodyScan,
  blocks: number,
): CutBody => {
  if (scan.bodyStart < 0) throw new Error('Document has no body')
  const truncated = !complete || scan.blockEnds.length > blocks || !scan.closed
  if (!truncated) return { xml: head, truncated }

  const kept = scan.blockEnds.slice(0, blocks)
  let end = kept.at(-1) ?? scan.bodyStart
  let tail = ''
  // One table can outrun the whole budget; keep the rows that did stream in.
  const open = scan.openBlock
  if (
    kept.length === scan.blockEnds.length &&
    open?.name === 'w:tbl' &&
    open.lastRowEnd > 0
  ) {
    end = open.lastRowEnd
    tail = '</w:tbl>'
  }
  const body = head.slice(scan.bodyStart, end) + tail
  const sectPr = firstSectPr(head.slice(scan.bodyStart))
  return {
    xml: `${head.slice(0, scan.bodyStart)}${body}${sectPr}</w:body></w:document>`,
    truncated,
  }
}

const STYLE = /<w:style\b[^>]*?(?:\/>|>[\s\S]*?<\/w:style>)/g
const STYLE_REFERENCE =
  /<w:(?:pStyle|rStyle|tblStyle|numStyleLink|styleLink)\b[^>]*?\bw:val="([^"]+)"/g
const STYLE_CHAIN = /<w:(?:basedOn|link|next)\b[^>]*?\bw:val="([^"]+)"/g

/**
 * The style sheet cut to the styles the kept blocks name, the ones they
 * inherit from, and the defaults. A stock template carries hundreds of
 * styles and latent styles; the parser reads every one before the first
 * paragraph draws.
 */
export const pruneStyles = (stylesXml: string, ...usedBy: string[]) => {
  const styles = new Map<string, string>()
  const defaults: string[] = []
  for (const [style] of stylesXml.matchAll(STYLE)) {
    const id = /\bw:styleId="([^"]+)"/.exec(style)?.[1]
    if (!id) continue
    styles.set(id, style)
    if (/\bw:default="(?:1|true)"/.test(style)) defaults.push(id)
  }
  const kept = new Set<string>()
  const pending = [
    ...defaults,
    ...usedBy.flatMap((xml) =>
      [...xml.matchAll(STYLE_REFERENCE)].map((match) => match[1]),
    ),
  ]
  while (pending.length > 0) {
    const id = pending.pop()!
    const style = styles.get(id)
    if (kept.has(id) || !style) continue
    kept.add(id)
    for (const match of style.matchAll(STYLE_CHAIN)) pending.push(match[1])
    for (const match of style.matchAll(STYLE_REFERENCE)) pending.push(match[1])
  }
  return stylesXml
    .replace(
      /<w:latentStyles\b[\s\S]*?<\/w:latentStyles>|<w:latentStyles\b[^>]*\/>/,
      '',
    )
    .replace(STYLE, (style) => {
      const id = /\bw:styleId="([^"]+)"/.exec(style)?.[1]
      return id && kept.has(id) ? style : ''
    })
}

/** Comment anchors point into a part that isn't kept. */
const withoutComments = (xml: string) =>
  xml
    .replace(/<w:comment(?:RangeStart|RangeEnd|Reference)\b[^>]*\/>/g, '')
    .replace(
      /<w:comment(?:RangeStart|RangeEnd|Reference)\b[^>]*>[\s\S]*?<\/w:comment(?:RangeStart|RangeEnd|Reference)>/g,
      '',
    )

/** Where Word puts the parts every document needs. */
const CONVENTIONAL_DOCUMENT = 'word/document.xml'
const CONVENTIONAL_PARTS = [
  '[Content_Types].xml',
  '_rels/.rels',
  'word/_rels/document.xml.rels',
  'word/styles.xml',
  'word/numbering.xml',
  'word/settings.xml',
  'word/theme/theme1.xml',
  'word/fontTable.xml',
  'word/footnotes.xml',
  'word/endnotes.xml',
]

/** The body's head, streamed until the preview has its blocks. */
const readBody = async (zip: OpenZip, path: string, blocks: number) => {
  const entry = zip.entries.get(path)
  if (!entry) throw new Error('Main document part is missing')
  const scanBody = bodyScanner()
  const head = await readEntryHead(zip.source, entry, (text) => {
    const scan = scanBody(text)
    return (
      scan.closed ||
      scan.blockEnds.length > blocks ||
      text.length - Math.max(0, scan.bodyStart) > DOCX_PREVIEW_MAX_CHARS
    )
  })
  // A body that streamed in whole never had `enough` see its last chunk.
  return cutDocument(head.text, head.complete, scanBody(head.text), blocks)
}

export const slimDocx = async (
  zip: OpenZip,
  name: string,
  { blocks = DOCX_PREVIEW_BLOCKS }: { blocks?: number } = {},
): Promise<File> => {
  // The body starts streaming from where Word puts it while the package's own
  // pointer to it is still in flight.
  prefetchParts(zip, CONVENTIONAL_PARTS, PREFETCH_MAX_BYTES)
  const guessedBody = zip.entries.has(CONVENTIONAL_DOCUMENT)
    ? readBody(zip, CONVENTIONAL_DOCUMENT, blocks)
    : null
  guessedBody?.catch(() => undefined)

  const root = decodeParts(
    await readParts(zip, ['[Content_Types].xml', '_rels/.rels']),
  )
  const documentPath = officeDocumentPath(root.get('_rels/.rels') ?? '')
  const documentRelsPath = getOpcRelationshipPartName(documentPath)
  const [cut, rels] = await Promise.all([
    documentPath === CONVENTIONAL_DOCUMENT && guessedBody
      ? guessedBody
      : readBody(zip, documentPath, blocks),
    readParts(zip, [documentRelsPath]),
  ])
  const documentXml = withoutComments(cut.xml)

  const documentRels = parseRels(decodeParts(rels).get(documentRelsPath) ?? '')
  const used = referencedRelIds(documentXml)
  const supportRels = documentRels.filter(
    (rel) => SUPPORT_TYPES.has(rel.type) && !rel.external,
  )
  const contentRels = documentRels.filter(
    (rel) =>
      used.has(rel.id) &&
      !SUPPORT_TYPES.has(rel.type) &&
      rel.type !== 'comments',
  )

  const supportPaths = supportRels
    .map((rel) => resolveTarget(documentPath, rel.target))
    .filter(
      (path) =>
        (zip.entries.get(path)?.uncompressedSize ?? Infinity) <=
        SUPPORT_PART_MAX_BYTES,
    )
  const files = new Map<string, string | Uint8Array>()
  const [support] = await Promise.all([
    readParts(zip, supportPaths),
    collectDependencies(
      zip,
      contentRels
        .filter((rel) => !rel.external)
        .map((rel) => resolveTarget(documentPath, rel.target)),
      files,
    ),
  ])
  for (const [path, bytes] of support) files.set(path, bytes)
  const stylesRel = supportRels.find((rel) => rel.type === 'styles')
  const stylesPath = stylesRel && resolveTarget(documentPath, stylesRel.target)
  const stylesBytes = stylesPath && support.get(stylesPath)
  if (stylesPath && stylesBytes) {
    const decoder = new TextDecoder()
    const others = [...files]
      .filter(([path]) => path !== stylesPath && path.endsWith('.xml'))
      .map(([, content]) =>
        typeof content === 'string' ? content : decoder.decode(content),
      )
    files.set(
      stylesPath,
      pruneStyles(decoder.decode(stylesBytes), documentXml, ...others),
    )
  }
  const keptSupport = supportRels.filter((rel) =>
    support.has(resolveTarget(documentPath, rel.target)),
  )

  files.set('_rels/.rels', rootRelsFor(root.get('_rels/.rels') ?? ''))
  files.set(documentPath, documentXml)
  files.set(documentRelsPath, writeRels([...keptSupport, ...contentRels]))
  files.set(
    '[Content_Types].xml',
    rewriteContentTypes(
      root.get('[Content_Types].xml') ?? '',
      new Set(files.keys()),
    ),
  )
  return packStore(files, DOCX_TYPE, name)
}
